import { PriorityActivityOverview } from './PriorityActivityOverview'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { type PlanOrganization } from '../../../../models/PlanOrganization'
import { GetPlanOrganization } from '../../../../services/AnnualPlanService'
import { AnnualPlanRole } from '../../../../models/AnnualPlanRole'
import { KeyboardBackspace } from '@mui/icons-material'
import { generatePath } from 'react-router'
import { AppRoutes } from '../../../Routes'
import { AnnualPlanTabs } from '../PlanningDetails'
import { useAnnualPlanUserRole } from '../../../../hooks/use-annualPlanUserRole'

export const PlanningPriorityActivitiesPlanOrganization = (): JSX.Element => {
  const nav = useNavigate()
  const { rcnId, planId, planOrgId } = useParams()
  const [planOrganization, setPlanOrganization] = useState<PlanOrganization>()
  const { userRole, isLoading: isUserRoleLoading, error: userRoleError } = useAnnualPlanUserRole(planId != null ? Number(planId) : 0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isImplementationPhase = window.location.pathname.includes('Implementation')

  useEffect(() => {
    const getPlanOrganization = async (): Promise<void> => {
      if (planOrgId == null || isNaN(Number(planOrgId))) {
        setError('Invalid Plan Organization ID.')
        return
      }

      try {
        setIsLoading(true)

        const { success, response: planOrg } = await GetPlanOrganization(Number(planOrgId))

        if (success) {
          setError(planOrg == null ? 'Plan Organization not found.' : null)
          setPlanOrganization(planOrg)
        } else {
          setError('Failed to fetch Plan Organization.')
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'An unknown error occurred while fetching Plan Organization.')
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    }

    void getPlanOrganization()
  }, [planOrgId])

  if (isLoading || isUserRoleLoading) {
    return <CircularProgress />
  }

  if (planOrganization == null || error != null || userRoleError != null) {
    return <Typography variant='body1' textAlign="center">{error ?? userRoleError}</Typography>
  }

  const showReviewOthersButton = userRole != null &&
    [AnnualPlanRole.PlanAdministrator, AnnualPlanRole.PlanContributor, AnnualPlanRole.PlanReviewer].includes(userRole)

  const handleReviewOthers = (): void => {
    if (rcnId == null || planId == null) return

    const route = generatePath(
      isImplementationPhase
        ? AppRoutes.IMPLEMENTATION_DETAILS
        : AppRoutes.PLANNING_DETAILS,
      { rcnId, planId, tab: String(AnnualPlanTabs.PriorityActivities) }
    )
    nav(`${route}?reviewOthers=true`)
  }

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  }}
  >
    {showReviewOthersButton &&
      <Button
        data-testid='reviewOtherIsdPrioritiesButton'
        sx={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          color: 'var(--grey-500)',
          width: 'fit-content'
        }}
        onClick={handleReviewOthers}
      >
        <KeyboardBackspace /> <Typography variant='body1'>Review Other ISD Priorities</Typography>
      </Button>}

    <PriorityActivityOverview planOrganization={planOrganization} implementationPhase={isImplementationPhase} />
  </Box>
}
